/**
 * Scene 3: The Journey Through Time
 * "2007 → 2025"
 */

import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as d3 from 'd3';
import { ChartLineIcon, WarningIcon, CalendarIcon } from '../../icons/StoryIcons';
import type { StoryInsights } from '../../../hooks/useStoryData';

gsap.registerPlugin(ScrollTrigger);

interface Scene3Props {
  insights: StoryInsights;
  isActive?: boolean;
}

// Key events for timeline annotations
const KEY_EVENTS = [
  { year: 2008, label: 'Global Financial Crisis', type: 'crisis' },
  { year: 2014, label: 'Oil Price Crash Begins', type: 'crisis' },
  { year: 2016, label: 'Recession', type: 'crisis' },
  { year: 2020, label: 'COVID-19 Pandemic', type: 'crisis' },
  { year: 2022, label: 'Post-Pandemic Recovery', type: 'recovery' },
];

export function Scene3Timeline({ insights, isActive }: Scene3Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<SVGSVGElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const pathRef = useRef<SVGPathElement>(null);
  const eventsRef = useRef<HTMLDivElement>(null);
  
  // Chart dimensions
  const width = 800;
  const height = 400;
  const margin = { top: 40, right: 40, bottom: 60, left: 80 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  useEffect(() => {
    if (!containerRef.current || !chartRef.current) return;
    
    const ctx = gsap.context(() => {
      // Only set initial hidden state if not active
      if (!isActive) {
        gsap.set(titleRef.current, { opacity: 0, y: 50 });
        gsap.set(eventsRef.current, { opacity: 0 });
        
        // Animate data points
        const dots = chartRef.current?.querySelectorAll('.data-point');
        if (dots) {
          gsap.set(dots, { scale: 0, opacity: 0 });
        }
      }
      
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top center',
          end: 'center center',
          toggleActions: 'play none none reverse',
        },
      });
      
      tl.to(titleRef.current, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      });
      
      // Animate chart line drawing
      if (pathRef.current) {
        const length = pathRef.current.getTotalLength();
        gsap.set(pathRef.current, {
          strokeDasharray: length,
          strokeDashoffset: length,
        });
        
        tl.to(pathRef.current, {
          strokeDashoffset: 0,
          duration: 2,
          ease: 'power2.inOut',
        }, '-=0.4');
      }
      
      // Animate events
      tl.to(eventsRef.current, {
        opacity: 1,
        duration: 0.8,
      }, '-=1');
      
      // Animate data points
      const dots = chartRef.current?.querySelectorAll('.data-point');
      if (dots) {
        tl.to(dots, {
          scale: 1,
          opacity: 1,
          duration: 0.4,
          stagger: 0.05,
          ease: 'back.out(2)',
        }, '-=1.5');
      }
    }, containerRef);
    
    return () => ctx.revert();
  }, []);
  
  // Force visibility when scene becomes active (for auto-scroll)
  useEffect(() => {
    if (isActive) {
      gsap.to(titleRef.current, { opacity: 1, y: 0, duration: 0.5 });
      gsap.to(eventsRef.current, { opacity: 1, duration: 0.5 });
      
      if (pathRef.current) {
        gsap.to(pathRef.current, { strokeDashoffset: 0, duration: 1 });
      }
      
      const dots = chartRef.current?.querySelectorAll('.data-point');
      if (dots) {
        gsap.to(dots, { scale: 1, opacity: 1, duration: 0.3, stagger: 0.02 });
      }
    }
  }, [isActive]);
  
  // Create scales
  const xScale = d3.scaleLinear()
    .domain([insights.yearsSpan.start, insights.yearsSpan.end])
    .range([0, innerWidth]);
  
  const yScale = d3.scaleLinear()
    .domain([0, d3.max(insights.yearlyTotals, d => d.total) || 0])
    .range([innerHeight, 0])
    .nice();
  
  // Create line generator
  const lineGenerator = d3.line<{ year: number; total: number }>()
    .x(d => xScale(d.year))
    .y(d => yScale(d.total))
    .curve(d3.curveMonotoneX);
  
  const linePath = lineGenerator(insights.yearlyTotals) || '';
  
  // Create area generator for gradient fill
  const areaGenerator = d3.area<{ year: number; total: number }>()
    .x(d => xScale(d.year))
    .y0(innerHeight)
    .y1(d => yScale(d.total))
    .curve(d3.curveMonotoneX);
  
  const areaPath = areaGenerator(insights.yearlyTotals) || '';
  
  // Filter events to only those within data range
  const visibleEvents = KEY_EVENTS.filter(
    e => e.year >= insights.yearsSpan.start && e.year <= insights.yearsSpan.end
  );
  
  return (
    <section
      ref={containerRef}
      className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden px-4 py-20"
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(
            90deg,
            #06b6d4 0px,
            #06b6d4 1px,
            transparent 1px,
            transparent 80px
          )`,
        }} />
      </div>
      
      {/* Content */}
      <div className="relative z-10 max-w-5xl w-full">
        {/* Title */}
        <div ref={titleRef} className="text-center mb-12">
          <div className="flex items-center justify-center gap-4 mb-4">
            <CalendarIcon size={40} className="text-accent-400" />
            <h2 className="text-3xl md:text-5xl font-display font-bold text-white">
              The Journey Through Time
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-surface-300">
            <span className="text-accent-400 font-bold">{insights.yearsSpan.start}</span>
            {' → '}
            <span className="text-accent-400 font-bold">{insights.yearsSpan.end}</span>
          </p>
        </div>
        
        {/* Chart */}
        <div className="bg-surface-900/50 backdrop-blur-sm rounded-2xl border border-surface-700 p-6 overflow-hidden transition-all duration-300 hover:bg-surface-900/70 hover:border-surface-600 hover:shadow-xl hover:shadow-accent-500/5">
          <svg
            ref={chartRef}
            viewBox={`0 0 ${width} ${height}`}
            className="w-full h-auto"
            preserveAspectRatio="xMidYMid meet"
          >
            <defs>
              <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
              </linearGradient>
              <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#22d3ee" />
                <stop offset="50%" stopColor="#06b6d4" />
                <stop offset="100%" stopColor="#0891b2" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            
            <g transform={`translate(${margin.left}, ${margin.top})`}>
              {/* Grid lines */}
              {yScale.ticks(5).map(tick => (
                <g key={tick}>
                  <line
                    x1={0}
                    x2={innerWidth}
                    y1={yScale(tick)}
                    y2={yScale(tick)}
                    stroke="#334155"
                    strokeDasharray="4,4"
                  />
                  <text
                    x={-10}
                    y={yScale(tick)}
                    textAnchor="end"
                    alignmentBaseline="middle"
                    className="text-xs fill-surface-500"
                  >
                    ₦{(tick / 1e12).toFixed(1)}T
                  </text>
                </g>
              ))}
              
              {/* X axis */}
              <g transform={`translate(0, ${innerHeight})`}>
                {insights.yearlyTotals
                  .filter((_, i) => i % 2 === 0)
                  .map(d => (
                    <text
                      key={d.year}
                      x={xScale(d.year)}
                      y={25}
                      textAnchor="middle"
                      className="text-xs fill-surface-400"
                    >
                      {d.year}
                    </text>
                  ))}
              </g>
              
              {/* Area fill */}
              <path
                d={areaPath}
                fill="url(#areaGradient)"
                className="transition-opacity duration-500"
              />
              
              {/* Line */}
              <path
                ref={pathRef}
                d={linePath}
                fill="none"
                stroke="url(#lineGradient)"
                strokeWidth="3"
                strokeLinecap="round"
                filter="url(#glow)"
              />
              
              {/* Data points */}
              {insights.yearlyTotals.map(d => (
                <circle
                  key={d.year}
                  cx={xScale(d.year)}
                  cy={yScale(d.total)}
                  r={4}
                  className="data-point fill-accent-400 stroke-surface-900"
                  strokeWidth="2"
                />
              ))}
              
              {/* Event markers */}
              {visibleEvents.map(event => {
                const yearData = insights.yearlyTotals.find(d => d.year === event.year);
                if (!yearData) return null;
                
                return (
                  <g key={event.year} className="event-marker">
                    <line
                      x1={xScale(event.year)}
                      x2={xScale(event.year)}
                      y1={yScale(yearData.total) - 10}
                      y2={0}
                      stroke={event.type === 'crisis' ? '#ef4444' : '#22c55e'}
                      strokeWidth="1"
                      strokeDasharray="4,4"
                      opacity="0.5"
                    />
                  </g>
                );
              })}
            </g>
          </svg>
          
          {/* Event labels */}
          <div ref={eventsRef} className="mt-6 flex flex-wrap justify-center gap-4">
            {visibleEvents.map(event => (
              <div
                key={event.year}
                className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer
                  transition-all duration-300 hover:scale-105 hover:-translate-y-0.5
                  ${event.type === 'crisis' 
                    ? 'bg-danger-500/20 text-danger-400 hover:bg-danger-500/30 hover:shadow-lg hover:shadow-danger-500/20' 
                    : 'bg-success-500/20 text-success-400 hover:bg-success-500/30 hover:shadow-lg hover:shadow-success-500/20'}
                `}
              >
                <WarningIcon size={14} className="transition-transform duration-300 group-hover:rotate-12" />
                <span className="font-medium">{event.year}</span>
                <span className="text-surface-400">·</span>
                <span>{event.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Peak & Low stats */}
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="group p-6 bg-success-500/10 rounded-xl border border-success-500/30 cursor-pointer transition-all duration-300 hover:bg-success-500/20 hover:border-success-500/50 hover:shadow-lg hover:shadow-success-500/10 hover:-translate-y-1 hover:scale-[1.02]">
            <div className="flex items-center gap-3 mb-2">
              <ChartLineIcon size={24} className="text-success-400 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3" />
              <span className="text-surface-400 uppercase text-sm tracking-wide transition-colors duration-300 group-hover:text-surface-300">Peak Year</span>
            </div>
            <div className="text-3xl font-display font-bold text-success-400 transition-all duration-300 group-hover:text-success-300 group-hover:scale-105">
              {insights.peakYear.year}
            </div>
            <div className="text-surface-300 transition-colors duration-300 group-hover:text-surface-200">
              ₦{(insights.peakYear.total / 1e12).toFixed(2)} Trillion distributed
            </div>
          </div>
          
          <div className="group p-6 bg-danger-500/10 rounded-xl border border-danger-500/30 cursor-pointer transition-all duration-300 hover:bg-danger-500/20 hover:border-danger-500/50 hover:shadow-lg hover:shadow-danger-500/10 hover:-translate-y-1 hover:scale-[1.02]">
            <div className="flex items-center gap-3 mb-2">
              <ChartLineIcon size={24} className="text-danger-400 rotate-180 transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-[177deg]" />
              <span className="text-surface-400 uppercase text-sm tracking-wide transition-colors duration-300 group-hover:text-surface-300">Lowest Year</span>
            </div>
            <div className="text-3xl font-display font-bold text-danger-400 transition-all duration-300 group-hover:text-danger-300 group-hover:scale-105">
              {insights.lowYear.year}
            </div>
            <div className="text-surface-300 transition-colors duration-300 group-hover:text-surface-200">
              ₦{(insights.lowYear.total / 1e12).toFixed(2)} Trillion distributed
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default Scene3Timeline;

